import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  Renderer2,
  signal,
  effect,
  TemplateRef,
  ViewChild,
  OnInit,
  DestroyRef,
} from '@angular/core';
import { DOCUMENT, NgClass, NgForOf, NgIf, NgTemplateOutlet } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { TSIconModule, TsInputComponent, TsMenuComponent } from 'tsui';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TsMenuItem } from 'tsui/lib/ts-menu/ts-menu.component';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { guid } from '@shared/Utils';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type XOR<T, U> =
  | (T & { [K in Exclude<keyof U, keyof T>]?: never })
  | (U & { [K in Exclude<keyof T, keyof U>]?: never });

export type TsTabDefinition<T = unknown> = XOR<{ template: TemplateRef<T> }, { path: string }> & {
  id?: string;
  label: string;
  icon?: string;
};

@Component({
  selector: 'ts-tab-group',
  standalone: true,
  imports: [
    MatTabsModule,
    NgTemplateOutlet,
    TSIconModule,
    NgIf,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    MatButtonModule,
    MatTooltipModule,
    TsMenuComponent,
    NgClass,
    RouterOutlet,
    NgForOf,
  ],
  templateUrl: './ts-tab-group.component.html',
  styleUrl: 'ts-tab-group.component.scss',
})
export class TsTabGroupComponent implements OnInit {
  @Input({ required: true }) tabs: TsTabDefinition[];
  @Input() set options(val: TsMenuItem[]) {
    this.menuOptions = [...val, ...this.menuOptions];
  }
  @Input() mode: 'default' | 'route' = 'default';
  @Input() enableOptions = false;
  @Input() enableDragging = false;
  @Input() enableCreation = false;
  protected selectedTabIndex = signal(0);

  @Output() tabsReordered = new EventEmitter<TsTabDefinition[]>();

  readonly renderer = inject(Renderer2);
  readonly docRef = inject(DOCUMENT);
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly destroyRef = inject(DestroyRef);
  readonly activeTabName = signal(null);
  readonly enableRename = signal(false);

  effectRef = effect(() => {
    if (this.enableRename()) {
      const activeInputElement = <HTMLInputElement>this.docRef.querySelector(`.input-tab-${this.selectedTabIndex()}`);

      setTimeout(() => {
        activeInputElement.focus();
        activeInputElement.select();
      }, 0);
    }
  });

  menuOptions: TsMenuItem[] = [
    {
      label: 'Rename Active Tab',
      action: () => {
        this.activeTabName.set(this.tabs[this.selectedTabIndex()].label);
        this.enableRename.set(true);
      },
    },
    {
      label: 'Delete Active Tab',
      icon: 'ts_trash-alt',
      variant: 'danger',
      disabled: () => this.isSingleTab,
      action: () => {
        if (this.isSingleTab) return;

        this.tabs.splice(this.selectedTabIndex(), 1);
        this.selectedTabIndex.set(0);
      },
    },
  ];

  @ViewChild('newTabDialog') tabCreationDialog: TemplateRef<any>;
  @ViewChild(TsInputComponent, { read: ElementRef }) activeTabRenameInput: ElementRef;
  activeLink = signal('');

  get isSingleTab() {
    return this.tabs?.length === 1;
  }

  ngOnInit() {
    if (this.mode === 'route') {
      this.router.events
        .pipe(
          filter((event) => {
            return event instanceof NavigationEnd || (event as any)?.routerEvent instanceof NavigationEnd;
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((event: any) => {
          const urlAfterRedirects =
            (event as NavigationEnd)?.urlAfterRedirects ||
            (event as { routerEvent: NavigationEnd }).routerEvent.urlAfterRedirects;
          const activeTab = this.findActiveTab(urlAfterRedirects);

          if (activeTab) {
            this.selectedTabIndex.set(this.tabs.indexOf(activeTab));
            this.activeLink.set(activeTab.path);
          }
        });
    }
  }

  private findActiveTab(urlAfterRedirects: string) {
    const cleanUrl = urlAfterRedirects.split('?')[0].split('#')[0];
    const urlSegments = cleanUrl.split('/').filter(Boolean).join('/');

    return this.tabs.find((tab) => new RegExp(`(^|/)${tab.path}(/|$)`).test(urlSegments));
  }

  drop(event: CdkDragDrop<string[]>) {
    if (event.previousIndex === event.currentIndex) return;

    const prevActive = this.tabs[this.selectedTabIndex()];
    this.tabs = [...this.tabs];
    moveItemInArray(this.tabs, event.previousIndex, event.currentIndex);
    this.selectedTabIndex.set(this.tabs.indexOf(prevActive));

    this.tabsReordered.emit([...this.tabs]);
  }

  createNewTab() {
    const newLabel = `Tab ${this.tabs.length + 1}`;

    this.tabs.push({
      id: guid(),
      label: newLabel,
      template: null,
    });
  }

  processRenaming($event: Event) {
    $event.stopPropagation();
    $event.stopPropagation();

    // if it is a keyboard event, we only want to process the enter key
    if ($event instanceof KeyboardEvent && $event.key === 'Enter') {
      this.tabs[this.selectedTabIndex()].label = this.activeTabName();
      this.activeTabRenameInput?.nativeElement.blur();
    }

    this.enableRename.set(false);
    this.activeTabName.set(null);
  }

  navigateToTab(path: string) {
    this.activeLink.set(path);

    this.router.navigate([path], { relativeTo: this.route });
  }
}
